import type { Platform } from '../../../shared/CharacterTypes';
import type { IGraphics } from '../../rendering/IRenderer';

type MaterialFamily = 'loam' | 'sand' | 'ice' | 'slate' | 'masonry' | 'crystal' | 'basalt';
export interface WorldTerrainMaterial {
  family: MaterialFamily;
  face: number;
  deep: number;
  cap: number;
  edge: number;
  /** Constructed decks meet authored support art across their whole underside. */
  flatUnderside?: boolean;
  /** Floor-resting masonry needs a visible body all the way to its footing. */
  groundedMasonry?: boolean;
}
const material = (family: MaterialFamily, face: number, deep: number, cap: number, edge: number): WorldTerrainMaterial =>
  ({ family, face, deep, cap, edge });
const M = {
  forest: material('loam', 0x3c4135, 0x222c28, 0x647650, 0x91a374),
  field: material('loam', 0x424638, 0x252f2d, 0x647d55, 0x9ba77b),
  autumn: material('loam', 0x5b4031, 0x382b29, 0x986e44, 0xc89c61),
  blossom: material('loam', 0x5b4149, 0x352c38, 0x967a80, 0xc3a298),
  marsh: material('slate', 0x354942, 0x202e30, 0x5e7770, 0x94a597),
  stone: material('slate', 0x48505b, 0x2a323f, 0x747e87, 0xaab2ae),
  sand: material('sand', 0x886748, 0x514238, 0xc1a278, 0xe6c998),
  ice: material('ice', 0x476576, 0x293d54, 0xa5ced7, 0xe0f1ef),
  ruin: material('masonry', 0x4a4955, 0x292b39, 0x818087, 0xaaa69a),
  temple: material('masonry', 0x655c54, 0x302f37, 0x998871, 0xcaba98),
  sunken: material('masonry', 0x3d585b, 0x233843, 0x748c83, 0xa1b8a8),
  cave: material('crystal', 0x424558, 0x23293b, 0x687b92, 0x9fbbc6),
  abyss: material('slate', 0x2c3b48, 0x16232e, 0x536a7a, 0x91a5b1),
  moonfall: material('slate', 0x384653, 0x1d2b37, 0x576f80, 0x9db7c4),
  fungal: material('loam', 0x3c3546, 0x242330, 0x617464, 0x94aa8d),
  sky: material('crystal', 0x695e7e, 0x38394f, 0xb5a8b0, 0xe2d5c5),
  ash: material('basalt', 0x4b4142, 0x29272d, 0x876655, 0xc39a77),
  corruption: material('basalt', 0x42394d, 0x262633, 0x62704f, 0x96a879),
  coil: material('masonry', 0x2b3734, 0x141e1e, 0x4c6054, 0x8a9b83),
};

/** Explicit map admission keeps both towns, their ports and test arenas unchanged. */
export const WORLD_TERRAIN_MATERIALS: Readonly<Record<string, WorldTerrainMaterial>> = {
  tomb_waking: M.ruin, village_arrival: M.autumn, first_field: M.field,
  pig_beach: M.sand, drowned_road: M.marsh, drowned_road_hush_nook: M.coil, lanternwood: M.forest,
  brackenmoor: M.marsh, coil_undercroft: M.coil, tidewend_outskirts: M.field,
  moonfall_hollow: M.moonfall, lanternroot_hollow: M.forest, moonfen_pasture: M.marsh,
  dark_forest: M.forest, deep_woods: M.forest, abandoned_camp: M.forest, old_grove: M.forest,
  frozen_lake: M.ice, crystal_caverns: M.cave,
  ancient_temple: M.temple, volcano_summit: M.ash, sunken_ruins: M.sunken,
  glowspore_grotto: M.fungal, biome_nexus: M.sky,
  amber_grove: M.autumn, sakura_vale: M.blossom, verdant_canopy: M.forest,
  whispering_bamboo: M.forest, sunlit_meadow: M.field, goldengrass_savanna: M.sand,
  scorched_dunes: M.sand, sunkissed_shore: M.sand, murkwater_swamp: M.marsh,
  windswept_moor: M.marsh, stoneveil_barrens: M.stone, stormbreak_cliffs: M.stone,
  frostspire_peaks: M.ice, smoldering_wastes: M.ash, blighted_wastes: M.corruption,
  bloodmoon_reach: M.ash, duskfall_vale: M.blossom, sunless_deep: M.abyss,
  aether_expanse: M.sky, hallowed_expanse: M.ruin, skyreach_isles: M.sky,
  sunken_drift: M.marsh, sky_whale: M.sky, void_serpent: M.cave, fossil_graveyard: M.sand,
};

export function worldMaterialFor(levelId: string, p: Platform): WorldTerrainMaterial | undefined {
  const authoredTerrain = /^(?:(?:hollow|lanternroot|moonfen|field|road|moor|beach|wood|village)_(?:ground|rim|ledge)|undercroft_(?:floor|landing)|driftwood|seastack)$/.test(String(p.properties?.visualStyle));
  if (!Object.prototype.hasOwnProperty.call(WORLD_TERRAIN_MATERIALS, levelId) || !['normal', 'ice'].includes(p.type)
    || p.id?.startsWith('wall') || (p.properties?.useAuthoredColor === true && !authoredTerrain)
    || p.properties?.visualStyle === 'hidden' || p.width < 24 || p.height < 12
    || ![p.x, p.y, p.width, p.height].every(Number.isFinite)) return undefined;
  const base = WORLD_TERRAIN_MATERIALS[levelId];
  const profile = levelId === 'coil_undercroft'
    && ['broken_pillar_1', 'broken_pillar_2', 'dais_platform'].includes(p.id ?? '')
    ? { ...base, groundedMasonry: true }
    : levelId === 'moonfall_hollow' && p.id === 'observatory_grand_dais'
      ? { ...base, family: 'masonry' as const, flatUnderside: true } : base;
  if (levelId === 'moonfen_pasture' && p.id === 'beacon_watchtower_high') {
    return { family: 'slate', face: 0x565646, deep: 0x303e38, cap: 0x91977b,
      edge: 0xb7bb97, flatUnderside: true };
  }
  if (levelId === 'abandoned_camp' && ['camp_watch_roof', 'camp_store_loft'].includes(p.id)) {
    return { family: 'slate', face: 0x685640, deep: 0x423a2c, cap: 0x93825b,
      edge: 0xb5a079, flatUnderside: true };
  }
  // Named world terrain keeps its authored body color while gaining the shared
  // cap/strata and one-way silhouette. Other authored landmarks stay untouched.
  return authoredTerrain && p.properties?.useAuthoredColor && /^#[0-9a-f]{6}$/i.test(p.color)
    ? { ...profile, face: Number.parseInt(p.color.slice(1), 16) } : profile;
}

type TerrainGraphics = Pick<IGraphics, 'fillRect' | 'fillPolygon'>;

/** Static, bounded cutaway shapes: the bright top is exactly the collision top.
 * All detail stays INSIDE the collider, so decoration never advertises a false ledge.
 * Complexity is capped independently of world width; there is no per-frame drawing. */
export function drawWorldPlatformMaterial(g: TerrainGraphics, p: Pick<Platform, 'x' | 'y' | 'width' | 'height' | 'isOneWay'>,
  m: WorldTerrainMaterial): void {
  const { x, y, width: w, height: h } = p;
  if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return;
  // One-way shelves collide at the top only; tapered rock/root undersides make
  // the upper route read as natural ledges without implying solid side walls.
  if (p.isOneWay && !m.flatUnderside) {
    g.fillPolygon([{ x, y }, { x: x + w, y }, { x: x + w * .96, y: y + h * .5 },
      { x: x + w * .82, y: y + h * .72 }, { x: x + w * .72, y: y + h },
      { x: x + w * .48, y: y + h * .76 }, { x: x + w * .31, y: y + h * .96 },
      { x: x + w * .13, y: y + h * .68 }, { x: x + w * .04, y: y + h * .38 }], m.deep, 1);
    g.fillPolygon([{ x, y }, { x: x + w, y }, { x: x + w * .96, y: y + h * .42 },
      { x: x + w * .72, y: y + h * .72 }, { x: x + w * .36, y: y + h * .58 },
      { x: x + w * .08, y: y + h * .48 }], m.face, 1);
  } else {
    g.fillRect(x, y, w, h, m.deep, 1);
    if (m.groundedMasonry) {
      g.fillRect(x, y, w, h, m.face, 1);
      const footing = Math.min(12, h * .16);
      g.fillRect(x, y + h - footing, w, footing, m.cap, .35);
    } else {
      g.fillPolygon([{ x, y }, { x: x + w, y }, { x: x + w, y: y + h * .72 },
        { x: x + w * .72, y: y + h * .86 }, { x: x + w * .36, y: y + h * .7 },
        { x, y: y + h * .88 }], m.face, 1);
    }
  }
  const cap = Math.min(h * .22, m.family === 'ice' ? 18 : 12);
  g.fillRect(x, y, w, Math.min(2, h), m.edge, .9);
  g.fillPolygon([{ x, y: y + Math.min(2, h) }, { x: x + w, y: y + Math.min(2, h) },
    { x: x + w, y: y + cap * .7 }, { x: x + w * .76, y: y + cap },
    { x: x + w * .44, y: y + cap * .65 }, { x: x + w * .16, y: y + cap },
    { x, y: y + cap * .8 }], m.cap, 1);
  const count = Math.max(1, Math.min(18, Math.ceil(w / 140)));
  const step = w / count;
  for (let i = 0; i < count; i++) {
    const left = x + step * i, inset = step * .12;
    const detailHeight = p.isOneWay ? h * .42 : h;
    const top = y + cap + Math.max(0, detailHeight - cap) * (.14 + (i % 3) * .12);
    if (m.family === 'ice' || m.family === 'crystal') {
      g.fillPolygon([{ x: left + inset, y: y + cap }, { x: left + step * .74, y: y + cap },
        { x: left + step * .43, y: y + (p.isOneWay ? h * .45 : h * .86) }], m.cap, m.family === 'ice' ? .28 : .16);
    } else if (m.family === 'masonry') {
      g.fillRect(left + inset, top, step * .7, Math.min(3, h * .04), m.deep, .7);
      g.fillRect(left + step * .5, y + cap, Math.min(2, step * .03), (detailHeight - cap) * .56, m.deep, .6);
    } else {
      const thickness = Math.min(h * .065, m.family === 'sand' ? 7 : 5);
      g.fillPolygon([{ x: left + inset, y: top }, { x: left + step * .8, y: top - cap * .15 },
        { x: left + step * .7, y: top + thickness }, { x: left + step * .2, y: top + thickness * 1.4 }],
      i % 2 ? m.deep : m.cap, m.family === 'sand' ? .3 : .2);
    }
  }
}
